import { useState, type FormEvent } from 'react';
import type { Note, NoteType } from '@otakotakdev/shared';
import {
  useNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
  useTags,
  useGoals,
} from '@/hooks/api';
import { Badge, EmptyState, ErrorState, ListSkeleton, Modal, PageHeader, Spinner } from '@/components/ui';

const NOTE_TYPES: NoteType[] = ['quick_note', 'idea', 'journal', 'reference', 'decision'];

interface NoteFormState {
  content: string;
  noteType: NoteType;
  goalId: string;
  tagNames: string;
}

function NoteForm({
  open,
  initial,
  goals,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: Note | null;
  goals: { id: string; title: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<NoteFormState>({
    content: initial?.content ?? '',
    noteType: initial?.noteType ?? 'quick_note',
    goalId: initial?.goalId ?? '',
    tagNames: initial ? initial.tags.map((t) => t.name).join(', ') : '',
  });
  const create = useCreateNote();
  const update = useUpdateNote();
  const mutation = initial ? update : create;
  const error = mutation.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.content.trim()) return;
    const tagNames = form.tagNames
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const payload = {
      content: form.content.trim(),
      noteType: form.noteType,
      goalId: form.goalId || null,
      tagNames,
    };
    if (initial) {
      update.mutate({ id: initial.id, ...payload }, { onSuccess: onSaved });
    } else {
      create.mutate({ ...payload, source: 'dashboard' }, { onSuccess: onSaved });
    }
  };

  return (
    <Modal open={open} title={initial ? 'Edit note' : 'New note'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="note-content" className="label">
            Content
          </label>
          <textarea
            id="note-content"
            className="input min-h-32"
            required
            maxLength={20000}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="note-type" className="label">
              Type
            </label>
            <select
              id="note-type"
              className="input"
              value={form.noteType}
              onChange={(e) => setForm({ ...form, noteType: e.target.value as NoteType })}
            >
              {NOTE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="note-goal" className="label">
              Related goal
            </label>
            <select
              id="note-goal"
              className="input"
              value={form.goalId}
              onChange={(e) => setForm({ ...form, goalId: e.target.value })}
            >
              <option value="">None</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="note-tags" className="label">
            Tags (comma-separated)
          </label>
          <input
            id="note-tags"
            className="input"
            placeholder="idea, work"
            value={form.tagNames}
            onChange={(e) => setForm({ ...form, tagNames: e.target.value })}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error instanceof Error ? error.message : 'Failed to save note'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={mutation.isPending || !form.content.trim()}
          >
            {mutation.isPending ? <Spinner label="Saving…" /> : initial ? 'Save changes' : 'Create note'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function NotesPage() {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<NoteType | ''>('');
  const [tagFilter, setTagFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Note | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Note | null>(null);

  const params: Record<string, unknown> = { limit: 100 };
  if (appliedSearch) params.q = appliedSearch;
  if (typeFilter) params.noteType = typeFilter;
  if (tagFilter) params.tag = tagFilter;

  const { data, isLoading, isError, error, refetch } = useNotes(params);
  const tagsQuery = useTags();
  const goalsQuery = useGoals({ status: 'active', limit: 100 });
  const remove = useDeleteNote();

  const goals = goalsQuery.data?.items ?? [];

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Notes"
        subtitle="Capture thoughts, ideas, and references"
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            + New note
          </button>
        }
      />

      <form onSubmit={submitSearch} className="mb-4 flex flex-wrap gap-2" role="search">
        <input
          className="input min-w-48 flex-1"
          placeholder="Search notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search notes"
          maxLength={100}
        />
        <button type="submit" className="btn-ghost">
          Search
        </button>
        <select
          className="input w-auto"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as NoteType | '')}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {NOTE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className="input w-auto"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          aria-label="Filter by tag"
        >
          <option value="">All tags</option>
          {(tagsQuery.data ?? []).map((t) => (
            <option key={t.id} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
      </form>

      {isLoading && <ListSkeleton rows={5} />}
      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load notes'}
          onRetry={() => void refetch()}
        />
      )}
      {data && data.items.length === 0 && (
        <EmptyState
          title="No notes found"
          hint={appliedSearch || typeFilter || tagFilter ? 'Try adjusting your filters.' : 'Capture your first note.'}
          action={
            <button
              type="button"
              className="btn-primary mt-2"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              New note
            </button>
          }
        />
      )}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          {data.items.map((note) => (
            <article key={note.id} className="card p-4">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge tone="accent">{note.noteType}</Badge>
                {note.tags.map((t) => (
                  <Badge key={t.id}>{t.name}</Badge>
                ))}
                <span className="ml-auto text-xs text-muted">
                  {new Date(note.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{note.content}</p>
              <div className="mt-3 flex justify-end gap-1.5">
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => {
                    setEditing(note);
                    setFormOpen(true);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-danger px-2 py-1 text-xs"
                  onClick={() => setConfirmDelete(note)}
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {formOpen && (
        <NoteForm
          key={editing?.id ?? 'new'}
          open={formOpen}
          initial={editing}
          goals={goals}
          onClose={() => setFormOpen(false)}
          onSaved={() => setFormOpen(false)}
        />
      )}

      <Modal open={confirmDelete !== null} title="Delete note?" onClose={() => setConfirmDelete(null)}>
        <p className="mb-4 text-sm text-muted">This note will be permanently deleted.</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={remove.isPending}
            onClick={() => {
              if (!confirmDelete) return;
              remove.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) });
            }}
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
